import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { Customer } from "./types";

export const ME_KEY = ["me"] as const;

/**
 * The server is the source of truth: the session lives in an httpOnly cookie and
 * `/auth/me` tells us who is signed in. Nothing auth-related is stored client-side.
 */
export function useAuth() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => (await api<{ customer: Customer | null }>("/auth/me")).customer,
    staleTime: 5 * 60 * 1000,
  });

  const setCustomer = useCallback((c: Customer | null) => qc.setQueryData(ME_KEY, c), [qc]);

  const logout = useCallback(async () => {
    await api("/auth/logout", { method: "POST" });
    qc.setQueryData(ME_KEY, null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "catalog" });
  }, [qc]);

  return { customer: me.data ?? null, isLoading: me.isLoading, setCustomer, logout };
}
